<!--
  Preloading strategy:
    - preference for KISSed unique small spinner for breadcrumber only
      rather than preloader placeholders
-->
<template>
  <div class="page-scheduling">

    <layout-teleport-to-breadcrumb>
      <layout-breadcrumb-loader
        :predicate="fetchState.status.value === 'success'"
      />
    </layout-teleport-to-breadcrumb>

    <div
      :class="[
        'page-scheduling__filters',
        { 'page-scheduling__filters--animate-busy': fetchState.status.value !== 'success' }
      ]"
    >

      <!-- Filter: date -->
      <ui-form-inner-icon
        class="page-scheduling__filters__date"
        :variant="UI.VARIANT.PRIMARY"
        name="today"
        :theme="UI.ICON_THEME.OUTLINED"
      >
        <ui-form-date-single
          v-model="selectedDate"
          :to-read-options="{
            inputFormat: 'YYYY-MM-DD',
            outputFormat: 'ddd ll'
          }"
          :to-raw-options="{
            inputFormat: 'ddd ll',
            outputFormat: 'YYYY-MM-DD'
          }"
          calendar-mode="calendar-only"
          today-selection
        />
      </ui-form-inner-icon>

      <!-- Filter: Leave_Type_ -->
      <ui-form-inner-icon
        class="page-scheduling__filters__types"
        name="label"
        :theme="UI.ICON_THEME.OUTLINED"
        :size="UI.SIZE.MEDIUM"
      >
        <ui-form-tree
          v-model="selected_leaveType"
          :options="options_leaveType"
          :size="UI.SIZE.MEDIUM"
          placeholder="Type de congé"
          multiple
          clearable
          :clearable-variant="UI.VARIANT.DANGER"
        >
          <!-- Input options -->
          <template #value-label="{ node }">
            <ui-badge-heavy
              :class="[
                'page-scheduling__filters__types__badge',
                `page-scheduling__filters__types__badge--${node.id}`
              ]"
            >
              <span>{{ printLeaveTypeWithOptions(node.id, parametersModule) }}</span>
            </ui-badge-heavy>
          </template>

          <!-- Tree options -->
          <template #option-label="{ node }">
            <ui-badge-heavy
              :class="[
                'page-scheduling__filters__types__badge',
                `page-scheduling__filters__types__badge--${node.id}`,
                'my-0.25'
              ]"
            >
              <span class="font-size-90">{{ printLeaveTypeWithOptions(node.id, parametersModule) }}</span>
              <span
                v-if="playschoolPrintLeaveTypeWithOptions(node.id, parametersModule)"
                class="page-scheduling__filters__types__badge__sub"
              >{{ playschoolPrintLeaveTypeWithOptions(node.id, parametersModule) }}</span>
            </ui-badge-heavy>
          </template>

        </ui-form-tree>
      </ui-form-inner-icon>

      <!-- Filter: Collaborator or Service -->
      <ui-form-inner-icon
        class="page-scheduling__filters__services"
        name="filter_list"
        :theme="UI.ICON_THEME.OUTLINED"
      >
        <ui-form-tree
          v-model="selected_serviceSlug"
          :options="options_service"
          placeholder="Filtrer par salarié"
          no-children-text="Aucun salarié"
          multiple
          searchable
          clearable
          indent
          :clearable-variant="UI.VARIANT.DANGER"
        >
          <!-- Input options -->
          <template #value-label="{ node }">
            <span>{{ node.label }}</span>
          </template>

          <!-- Tree options -->
          <template #option-label="{ node }">

            <span
              v-if="node.isBranch"
            >
              {{ node.label }}
            </span>

            <span
              v-if="!node.isBranch"
              class="font-size-90"
            >
              {{ node.label }}
            </span>

          </template>
        </ui-form-tree>
      </ui-form-inner-icon>

    </div>

    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a1"
    >

      <div
        :class="[
          'page-scheduling__body',
          {
            'page-scheduling--animate-busy': isPending
          }
        ]"
      >

        <div class="page-scheduling__grid-header">

          <div class="page-scheduling__navigation">

            <ui-wad class="d-flex gap-1.5">

              <ui-button-icon
                class="page-scheduling__navigation__cta"
                name="navigate_before"
                :variant="UI.VARIANT.DARK"
                :elevation="UI.ELEVATION.LEVEL_2"
                :status="isPending ? UI.STATUS.MUTED : UI.STATUS.ENABLED"
                square
                ripple
                @click="previousWeekCallback"
              />

              <ui-button-icon
                class="page-scheduling__navigation__cta"
                name="navigate_next"
                :variant="UI.VARIANT.DARK"
                :elevation="UI.ELEVATION.LEVEL_2"
                :status="isPending ? UI.STATUS.MUTED : UI.STATUS.ENABLED"
                square
                ripple
                @click="nextWeekCallback"
              />

            </ui-wad>

          </div>

          <div class="page-scheduling__timeline">

            <div class="page-scheduling__timeline__week-row">

              <div
                v-for="week in weekList"
                :key="`week-${week.number}`"
                class="page-scheduling__timeline__week-cell"
              >
                <div
                  v-if="week.number === '1'"
                  :class="[
                    'page-scheduling__timeline__week-cell__year',
                    'page-scheduling__timeline__week-cell__year--beginning'
                  ]"
                >
                  {{ getYear( week.sunday ) }}
                </div>

                <div
                  v-if="isLastWeekOfYear(
                    week.monday,
                    week.number
                  )"
                  :class="[
                    'page-scheduling__timeline__week-cell__year',
                    'page-scheduling__timeline__week-cell__year--ending'
                  ]"
                >
                  {{ getYear( week.monday ) }}
                </div>

                <div class="page-scheduling__timeline__week-cell__months">

                  <div class="page-scheduling__timeline__week-cell__months__monday">
                    {{ getMonth( week.monday ) }}
                  </div>

                  <div
                    v-if="getMonth(week.monday) !== getMonth(week.sunday)"
                    class="page-scheduling__timeline__week-cell__months__sunday"
                  >
                    {{ getMonth( week.sunday) }}
                  </div>

                </div>


                <div class="page-scheduling__timeline__week-cell__content">
                  Semaine {{ week.number }}
                </div>

              </div>

            </div>

            <div class="page-scheduling__timeline__day-row">

              <div
                v-for="dateObject in dateList"
                :key="`day-${getDate(dateObject)}`"
                :data-day="getDate(dateObject)"
                :class="[
                  'page-scheduling__timeline__day-cell',
                  {
                    'page-scheduling__timeline__day-cell--sunday':
                      dateObject.getDay() === 0,
                    'page-scheduling__timeline__day-cell--selected':
                      selectedDate === getDate(dateObject),
                    'page-scheduling__timeline__day-cell--first':
                      dateObject.getDate() === 1
                  }
                ]"
              >
                {{ getDay(dateObject) }}

                <div class="page-scheduling__timeline__day-cell__tooltip">
                  {{ getDate_(dateObject) }}
                </div>
              </div>

            </div>

          </div>

        </div>

        <div class="page-scheduling__modular-grid">

          <div
            v-for="service in displayedServiceCollection"
            :key="service.uuidTeam"
          >

            <div class="page-scheduling__modular-grid__row">
              <div class="page-scheduling__modular-grid__cell-team">
                {{ service.name }}
              </div>

              <div
                v-for="dateObject in dateList"
                :key="`placeholder-cell-${getDate(dateObject)}`"
                class="page-scheduling__modular-grid__cell-placeholder"
                @mouseover="() => onCellHover('', dateObject)"
                @mouseleave="() => onCellBlur()"
              />

            </div>

            <div
              v-for="collaborator in service.collaboratorList"
              :key="collaborator.matricule"
              class="page-scheduling__modular-grid__row"
            >
              <div
                :class="[
                  'page-scheduling__modular-grid__cell-member',
                ]"
                :data-matricule="collaborator.matricule"
              >
                {{ collaborator.nom }}
                {{ collaborator.prenom }}
              </div>

              <div
                v-for="dateObject in dateList"
                :key="`main-cell-${collaborator.matricule}-${getDate(dateObject)}`"
                class="page-scheduling__modular-grid__cell-main"
                @mouseover="() => onCellHover(collaborator.matricule, dateObject)"
                @mouseleave="() => onCellBlur()"
              >

                <!--
                  (?) quick workaround saving some performance (by decreasing
                      the overall number of tiles to add to the DOM)
                -->
                <template v-if="hasAnyTile(collaborator.matricule, dateObject)">

                  <scheduling-half-day-tile
                    :key="`half-day-cell-${collaborator.matricule}-${getDate(dateObject)}-am`"
                    :class="[
                      'page-scheduling__modular-grid__cell-main__half-day',
                      'page-scheduling__modular-grid__cell-main__half-day--am'
                    ]"
                    v-bind="{
                      selectionCallback: schedulingDaySelectionCallback,
                      mSchedulingDay: extractTileData(
                        collaborator.matricule,
                        dateObject,
                        PERIOD.AM
                      ),
                    }"
                  />

                  <scheduling-half-day-tile
                    :key="`half-day-cell-${collaborator.matricule}-${getDate(dateObject)}-pm`"
                    :class="[
                      'page-scheduling__modular-grid__cell-main__half-day',
                      'page-scheduling__modular-grid__cell-main__half-day--pm'
                    ]"
                    v-bind="{
                      selectionCallback: schedulingDaySelectionCallback,
                      mSchedulingDay: extractTileData(
                        collaborator.matricule,
                        dateObject,
                        PERIOD.PM
                      )
                    }"
                  />

                </template>

              </div>

            </div>

          </div>

        </div>

      </div>

    </transition>

    <!-- MODAL: scheduling day details -->
    <ui-modal
      :id="SCHEDULING_DAY_DETAILS_MODAL_NAME"
      :size="UI.SIZE.SMALL"
      footer-class="justify-content-flex-start"
    >
      <template #title>
        Détails de la demande
        <ui-wad class="font-weight-light font-size-85">
          <ui-icon
            name="person"
            class="font-size-110 vertical-align-text-bottom"
            :theme="UI.ICON_THEME.OUTLINED"
          />
          {{ selectedSchedulingRequest.nom }}
          &hairsp;
          {{ selectedSchedulingRequest.prenom }}
        </ui-wad>
      </template>

      <scheduling-request-details
        :request="selectedSchedulingRequest"
        :days-calculation-type="daysCalculationType"
        :parameters-module="parametersModule"
      />

      <template #footer>
        <ui-button
          :size="UI.SIZE.SMALL"
          :variant="UI.VARIANT.LIGHT"
          @click="hideModal(SCHEDULING_DAY_DETAILS_MODAL_NAME)"
        >
          Fermer
        </ui-button>
      </template>
    </ui-modal>
  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/layout/members
@import ~/assets/sass/modules/transat/variables
@import ~/assets/sass/modules/transat/members
@import ~/assets/sass/modules/revogrid/variables

.page-scheduling
  $self: &

  margin-top: $layout-spacer-y

  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y

  /////////////////////////////////

  &__filters
    // @XXX Vue3 lack of State-Driven Dynamic CSS API
    $filter-date-width: calc(240px - ($layout-spacer-x * .5))   // (ie. `DETAILS_COLUMN` minus margin)
    $filter-type-width: 208px // (ie. a week size)

    margin-bottom: $layout-spacer-y
    display: flex
    align-items: flex-start
    gap: space-x(2)

    &--animate-busy
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

    &__date
      width: $filter-date-width

    &__services
      flex-grow: 1

    &__types
      min-width: $filter-type-width

      &__badge
        @include leave-request-type-badge

        // (empirical:  fitting <ui-form-tree size="sm">)
        $padding-y: 4px

        border-radius: initial // seamlessly embed the badge into the filter item
        padding-top: $padding-y
        padding-bottom: $padding-y

        &__sub
          font-size: 80%
          text-transform: lowercase
          font-weight: $font-weight-normal
          vertical-align: bottom
          margin-left: space-x(1)

      /// Customise <ui-form-tree> labels / options
      :deep(.ui-form-tree)

        .vue3-treeselect__multi-value-item
          // Visually increase the badge
          padding-top: initial
          padding-bottom: initial
          border: initial

        .vue3-treeselect__multi-value-label
          padding-left: initial
          padding-right: initial

        #{ $self }__filters__types__badge
          $line-height: 16px // empirical: increasing at ideal height

          line-height: $line-height
          border-radius: initial // seamlessly embed the badge into the filter item

    /// Melting filter input with its parent container background (preventing
    /// too much affordance for these UI elements, as they are just distant
    /// setting features)
    :deep(.form-control:not(:active):not(:focus)),
    :deep(.ui-form-tree:not(.vue3-treeselect--focused) .vue3-treeselect__control)
      border-color: $gray-bright

    /// Remove focus ring ("aesthetic" choice, as filters and gutters are very
    /// tiny, we do not want a focus ring overlap)
    // @TODO: "no-focus-ring" prop?
    :deep(.ui-form-input.form-control:focus),
    :deep(.ui-form-date-single .form-control:focus),
    :deep(.ui-form-tree .vue3-treeselect__control)
      box-shadow: initial

  ///////////////////////////////////////

  $teaser-height: 16px

  &__grid-header
    display: flex
    position: sticky
    top: $header-simulated-height
    background-color: $schedule-grid-bg
    padding-top: $layout-spacer-y * .5
    z-index: z-index($z-indexes, 'main', 'ceil')

  ///////////////////////////////////////

  // @XXX Vue3 lack of State-Driven Dynamic CSS API
  $cell-size: 28px        // (from `CELL_SIZE`)
  $gutter: 2px            // (from `GUTTTER`)
  $details-column: 240px  // (from `DETAILS_COLUMN`)

  &__navigation
    $cta-padding: space-x(.5)

    width: $details-column
    padding-left: $cta-padding
    padding-top: $cta-padding

    &__cta
      color: $white

      &::before
        @include inset( - $cta-padding )
        background-color: $primary
        // border-radius: initial // coherence with the "mosaic" feeling

      :deep(.ui-button-icon__ripple)
        @include inset( - $cta-padding )

  ///////////////////////////////////////

  &__timeline
    margin-bottom: $gutter * .5

    &__week-row
      display: flex
      padding-left: $gutter * .5

    &__week-cell
      width: ($cell-size * 7) + ($gutter * 7)
      background-color: $schedule-block-bg
      border-right: $gutter solid $body-bg
      position: relative

      &:first-of-type
        border-top-left-radius: $card-border-radius
      &:last-of-type
        border-top-right-radius: $card-border-radius

      &__year
        position: absolute
        top: -1
        padding: space-x(.5) space-x(1)
        font-size: 80%
        color: $gray-deep
        font-weight: $font-weight-bolder
        background-color: $schedule-block-bg
        transform: translateY( calc(-100% + $gutter) )

        &--ending
          right: 0

        &--beginning
          left: 0

      &__months
        display: flex
        justify-content: space-between
        font-size: 80%
        line-height: 1
        color: $gray-deep
        font-weight: $font-weight-bold

        &__monday
          position: relative
          top: space-x(1)
          left: space-x(1)

        &__sunday
          position: relative
          top: space-x(1)
          right: space-x(1)

      &__content
        font-size: 80%
        font-weight: $font-weight-light
        text-transform: uppercase
        color: $gray-deep
        line-height: 3
        text-align: center

    &__day-row
      display: flex
      padding-left: ($gutter * 0.5)

    &__day-cell
      width: ($cell-size + $gutter)
      line-height: $cell-size
      display: flex
      align-items: center
      justify-content: center
      font-size: 75%
      background-color: $schedule-block-bg
      border-right: $gutter solid transparent
      position: relative

      &__tooltip
        @include no-interaction
        display: none
        background-color: brighten($tertiary, 80%)
        color: $body-color
        position: absolute
        width: max-content
        font-size: 100%
        font-weight: $font-weight-bolder
        line-height: initial
        padding: space-x(.5) space-x(1)
        bottom: 0
        transform: translateY(100%)

        &::before
          $size: 6px

          @include virtual-content
          position: absolute
          border-left: $size solid transparent
          border-right: $size solid transparent
          border-top: $size solid brighten($tertiary, 80%)
          left: 50%
          bottom: 0
          transform: translateX(-50%) translateY(100%)

      &--sunday
        border-right-color: $schedule-grid-bg

      &--first
        box-shadow: (- $gutter) 0 0 0 $schedule-grid-bg

      &--selected
        background-color: $primary
        color: $white

      &--focused
        background-color: brighten($tertiary, 80%)
        color: $body-color

      &--focused &__tooltip
        display: initial

      &--focused#{&}--selected,
      &--focused#{&}--selected &__tooltip
        background-color: $primary
        color: $white

      &--focused#{&}--selected &__tooltip::before
        border-top-color: $primary

  ///////////////////////////////////////

  &__modular-grid

    &__row
      display: flex

    &__cell-team
      flex-basis: $details-column
      // background-color: $schedule-base-color
      background-color: $schedule-block-bg
      line-height: $cell-size
      padding-left: space-x(1)
      padding-right: space-x(1)
      border: ($gutter * .5) solid $schedule-grid-bg
      border-left: none
      white-space: nowrap
      overflow-x: hidden
      text-overflow: ellipsis
      font-family: $font-family-table
      color: $gray-40
      font-size: 13px
      border-top-left-radius: $card-border-radius
      border-bottom-left-radius: $card-border-radius

    &__cell-member
      width: $details-column
      line-height: $cell-size
      padding-left: space-x(2)
      padding-right: space-x(.5)
      font-size: 13px
      border: ($gutter * .5) solid $white
      border-left: none
      white-space: nowrap
      overflow-x: hidden
      text-overflow: ellipsis
      font-family: $font-family-table
      color: $gray-30
      border-top-left-radius: $card-border-radius
      border-bottom-left-radius: $card-border-radius

      &--focused
        background-color: brighten($tertiary, 80%)
        color: $body-color

    &__cell-placeholder
      width: $cell-size + $gutter
      height: $cell-size + $gutter
      background-color: $schedule-grid-bg


    &__cell-main
      width: $cell-size + $gutter
      height: $cell-size + $gutter
      border: ($gutter * .5) solid $schedule-grid-bg
      display: flex
      border-radius: $schedule-tile-radius

      // (?) due to a quick workaround on the HTML template, we paint the cell
      //     with a default colour (which would otherwise be painted within
      //     <half-day-tile>, but we optimised the number of HTMLElement
      //     integrated within the DOM)
      background-color: $schedule-base-color

      &__half-day
        flex-basis: 50%

        &--am,
        &--am > *
          border-top-left-radius: $schedule-tile-radius
          border-bottom-left-radius: $schedule-tile-radius

        &--pm,
        &--pm > *
          border-top-right-radius: $schedule-tile-radius
          border-bottom-right-radius: $schedule-tile-radius

  ///////////////////////////////////////

  /// Deffered state strategy:
  ///   - simple pattern (same as a <ui-table> UI pending state handling)
  ///   - applying fade transition
  ///
  &--animate-busy
    @include no-interaction
    opacity: 0.7
    transition: 0.3s
    transition-delay: 0.3s

</style>

<style lang="scss">
@import '~/assets/sass/modules/layout/variables';

/// Print
///
@media print {

  body.page-conges-scheduling {

    /// Removing beams
    // .layout-saas__header,
    .the-menu-sidebar,
    .layout-saas__body::before {
      display: none;
    }

    .layout-saas__body-wrapper {
      margin-top: initial;
    }

    .layout-saas__header {
      position: static;
    }

    /// Removing specific page controls
    .product-transat__nav {
      display: none;
    }

    .page-scheduling__filters * {
      border: initial;
      background: inherit;
    }

    .page-scheduling__navigation {
      visibility: hidden;
    }

    /// Resetting background
    background-color: $true-white;
    .product-transat,
    .page-scheduling__grid-header,
    .page-scheduling__modular-grid__cell-placeholder {
      background-color: $true-white;
    }
    .page-scheduling__modular-grid__cell-main {
      background-color: $true-gray-99;
    }

  }
}
</style>

<script lang="ts">
import { abs, add, divide, floor, mod, subtract } from 'mathjs'
import SchedulingHalfDayTile from '~/components/transat/scheduling/SchedulingHalfDayTile.vue'
import Console from '~/utils/lib/core/console'
import { dateDifference, datetimeToDateObject, now, parseDateObject, parseDatetime, getYearISOWeekCount } from '~/utils/lib/core/datetime'
import { debounce, dec, find, inc, Maybe, multiply, not, pathOr, pluck, tapCatch, times, toInteger, lengthNotEq, notEmpty, concatAll, equals, pipe_, toString, __, or, path } from '~/utils/lib/core/prelude'
import { useStore_transat_scheduling } from '~/stores/transat/scheduling'
import { castToDictionary, type SchedulingDay, type SchedulingDictionary, type SchedulingRequestLeave, type SchedulingRequestRemoteWork, useStore_transat_scheduling_v2 } from '~/stores/transat/scheduling/v2'
import SchedulingRequestDetails from '~/components/transat/scheduling/RequestDetails.vue'
import { type LEAVE_TYPE_, type DAYS_CALCULATION, generateLeaveOptionsForFilters, printLeaveTypeWithOptions, playschoolPrintLeaveTypeWithOptions } from '~/stores/transat/leave-request';
import { type UITreeOption } from '~/components/ui/form/Tree.vue'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useLegacyModalController } from '~/composables/layout/modal'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { querySelector } from '~/utils/lib/core/dom'
import { useAccessControl } from '~/composables/layout/access-control'
import type { Service_ } from '~/stores/transat/service'
import { useServiceTreeselect } from '~/composables/transat/service-treeselect.composable'
import { useStore_transat_parameters_module, type LeaveModuleInterface } from '~/stores/transat/parameters/module'
import { PERIOD } from '~/stores/transat/remote-work'
import { isHoliday, isLeaveType, isRemoteWork, isWeekend } from '~/stores/transat/scheduling/v2/index_'
import { useStore_transat_layout } from '~/stores/transat/layout'

const SCHEDULING_DAY_DETAILS_MODAL_NAME = `transat-scheduling-day-details-modal-name`

const CONTAINER_PADDING = 32  // (in px) (from $layout-spacer-x)
const DETAILS_COLUMN = 240    // (in px)
const GUTTER = 2              // (in px)
const CELL_SIZE = 28          // (in px)

const RESIZE_DEBOUNCE_DELAY = 300

const console = new Console('component:scheduling')

type Week
  = { monday: string
    , sunday: string
    , number: string
    }

export default defineComponent({
  components: { SchedulingHalfDayTile, SchedulingRequestDetails },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Planning des équipes — ${navigationContext.fromCompanyLevel().structure.raison}`,
      bodyAttrs: {
        class: 'page-conges-scheduling'
      }
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const {
      hideModal,
      showModal,
    } = useLegacyModalController()

    const ToastBuilder = useToastBuilder()

    const serviceList = ref<Service_[]>([])
    const {
      options_service,
      selected_serviceSlug,
      selected_matriculeId,
      selected_serviceId,
      getSelectedCollaboratorGroupedByService,
    } = useServiceTreeselect(
      serviceList,
    )

    const store_transat_layout = useStore_transat_layout()
    const store_scheduling = useStore_transat_scheduling()
    const store_scheduling_v2 = useStore_transat_scheduling_v2()
    const store_parameters_module = useStore_transat_parameters_module()

    store_transat_layout.setNavSection('scheduling')

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      hideModal,
      showModal,
      ToastBuilder,
      serviceList,
      options_service,
      selected_serviceSlug,
      selected_matriculeId,
      selected_serviceId,
      getSelectedCollaboratorGroupedByService,
      store_scheduling,
      store_scheduling_v2,
      store_parameters_module,
    }
  },
  data() {
    const today = now(
      {
        outputFormat: 'YYYY-MM-DD'
      }
    )

    return {
      Maybe,
      // scheduling
      parametersModule: null as unknown as LeaveModuleInterface, // (lazy)
      displayedSchedulingDictionary: null as unknown as SchedulingDictionary, // (lazy)
      displayedServiceCollection: null as unknown as Service_[], // (lazy)
      weekList: [] as Week[],
      dateList: [] as Date[],
      focusedDate: '',
      focusedMatricule: '',
      resizeEventReference: null as unknown as Function, // (lazy)
      // filters
      selectedDate: today,
      dateMin: '',
      dateMax: '',
      selected_leaveType: [] as LEAVE_TYPE_[],
      options_leaveType: [] as UITreeOption[], // (lazy)
      // modal
      SCHEDULING_DAY_DETAILS_MODAL_NAME,
      selectedSchedulingRequest: null as unknown as (SchedulingRequestLeave | SchedulingRequestRemoteWork),
      daysCalculationType : '' as unknown as DAYS_CALCULATION, // lazy
      // helpers
      isPending: false,
      printLeaveTypeWithOptions,
      playschoolPrintLeaveTypeWithOptions,
      isRemoteWork,
      PERIOD,
    }
  },
  watch: {
    selectedDate: {
      immediate: false,
      handler(selectedDate) {
        const hasFetched = this.fetchState.status.value === 'success'

        if (not(hasFetched))
          return

        const fromDate = findNearestAnteriorMonday(selectedDate)

        this.updateTimeline(fromDate)
      }
    },
    selected_serviceSlug: {
      immediate: false,
      handler(value) {
        const hasFetched = this.fetchState.status.value === 'success'

        if (not(hasFetched))
          return

        this.updateData()
      }
    },
    selected_leaveType: {
      immediate: false,
      handler(value) {
        const hasFetched = this.fetchState.status.value === 'success'

        if (not(hasFetched))
          return

        this.updateData()
      }
    },
    /// Defensive: prevent Vue3 to re-mount component and child component when
    ///            focused flag alters component class
    ///          ↳ workaround: use vanilly JavaScript to alter component class
    ///            instead of `:class` directive
    focusedDate: {
      immediate: false,
      handler(newVal, oldVal) {

        if (notEmpty(oldVal)) {
          const query = concatAll([
            `.page-scheduling__timeline__day-cell`,
            `[data-day="${oldVal}"]`
          ])

          querySelector(query)
            .map(el => {
              el.classList.remove(`page-scheduling__timeline__day-cell--focused`)
            })
        }

        if (notEmpty(newVal)) {
          const query = concatAll([
            `.page-scheduling__timeline__day-cell`,
            `[data-day="${newVal}"]`
          ])

          querySelector(query)
            .map(el => {
              el.classList.add(`page-scheduling__timeline__day-cell--focused`)
            })
        }
      }
    },
    /// Defensive: prevent "vue3" to re-mount component and child component when
    ///            focused flag alters component class
    ///          ↳ workaround: use vanilly JavaScript to alter component class
    ///            instead of `:class` directive
    focusedMatricule: {
      immediate: false,
      handler(newVal, oldVal) {

        if (notEmpty(oldVal)) {
          const query = concatAll([
            `.page-scheduling__modular-grid__cell-member`,
            `[data-matricule="${oldVal}"]`
          ])

          querySelector(query)
            .map(el => {
              el.classList.remove(`page-scheduling__modular-grid__cell-member--focused`)
            })
        }

        if (notEmpty(newVal)) {
          const query = concatAll([
            `.page-scheduling__modular-grid__cell-member`,
            `[data-matricule="${newVal}"]`
          ])

          querySelector(query)
            .map(el => {
              el.classList.add(`page-scheduling__modular-grid__cell-member--focused`)
            })
        }
      },
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  mounted() {
    this.resizeEventReference = debounce(
      RESIZE_DEBOUNCE_DELAY,
      () => {
        const hasFetched = this.fetchState.status.value === 'success'

        if (not(hasFetched))
          return

        const fromDate = findNearestAnteriorMonday(this.selectedDate)

        this.updateTimeline(fromDate)
      }
    )
    // @ts-expect-error: TypeScript limitation with EventListener
    window.addEventListener(
      'resize',
      this.resizeEventReference
    )
  },
  unmounted() {
    // @ts-expect-error: TypeScript limitation with EventListener
    window.removeEventListener(
      'resize',
      this.resizeEventReference
    )
  },
  methods: {
    getDate: dateObject => parseDateObject(
      {
        outputFormat: 'YYYY-MM-DD'
      },
      dateObject
    ),
    getDate_: dateObject => parseDateObject(
      {
        outputFormat: 'ddd ll'
      },
      dateObject
    ),
    getDay: dateObject => parseDateObject(
      {
        outputFormat: 'D'
      },
      dateObject
    ),
    getWeek: dateObject => parseDateObject(
      {
        outputFormat: 'w'
      },
      dateObject
    ),
    getMonth: dateObject => parseDateObject(
      {
        outputFormat: 'MMM'
      },
      dateObject
    ),
    getYear: dateObject => parseDateObject(
      {
        outputFormat: 'YYYY'
      },
      dateObject
    ),
    isLastWeekOfYear(
      date :string,
      weekNumber :string
    ) {
      const weekNumber_ = toInteger(weekNumber)

      const year = parseDatetime(
        {
          outputFormat: 'YYYY'
        },
        date
      )

      const weekCount = getYearISOWeekCount(
        {
          inputFormat: 'YYYY'
        },
        year
      )

      return equals(weekNumber_, weekCount)
    },
    hasAnyTile(
      matricule :string,
      dateObject :Date,
    ) {
      const dayKey = this.getDate(dateObject)
      const dayList = pathOr(
        [],
        [ matricule, dayKey ],
        this.displayedSchedulingDictionary
      )

      return lengthNotEq(0, dayList)
    },
    extractTileData(
      matricule: string,
      dateObject: Date,
      period: PERIOD
    ) {
      const dayKey = this.getDate(dateObject)
      const dayList = pathOr(
        [],
        [ matricule, dayKey ],
        this.displayedSchedulingDictionary
      )
      const data = find(
        (day: SchedulingDay) => {
          switch (true) {
            case (isLeaveType(day) || isRemoteWork(day)) &&
              or(
                equals(path(['period'], day), period),
                equals(path(['period'], day), PERIOD.FULL),
              ):
                return true
            case or(isWeekend(day), isHoliday(day)):
              return true
            default:
              return false
          }
        },
        dayList
      )

      return Maybe.toMaybe(data)
    },
    onCellHover(
      matricule :string,
      dateObject :Date
    ) {
      this.focusedDate = this.getDate(dateObject)
      this.focusedMatricule = matricule
    },
    onCellBlur() {
      this.focusedDate = ''
      this.focusedMatricule = ''
    },
    previousWeekCallback() {
      const fromDate = previousMonday(this.dateMin)

      this.updateTimeline(fromDate)
    },
    nextWeekCallback() {
      const fromDate = nextMonday(this.dateMin)

      this.updateTimeline(fromDate)
    },
    schedulingDaySelectionCallback(
      schedulingDay: SchedulingDay
    ) {
      switch (true) {
        case isLeaveType(schedulingDay):
        case isRemoteWork(schedulingDay):
          this.selectedSchedulingRequest = schedulingDay
          this.showModal(SCHEDULING_DAY_DETAILS_MODAL_NAME)
          break
      }
    },
    fetch() {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return Promise.all([
        this.store_scheduling.readService(options),
        this.store_parameters_module.read(options)
      ])
      .then(([hasAccess, parametersModule]) => {
        this.serviceList = hasAccess.data
        this.daysCalculationType = hasAccess.modeDecompte
        this.parametersModule = parametersModule
      })
      // Launch timeline set up & data hydration
      // (basically by simulating the "this.$watchers" for "selectedDate")
      .then(() => {
        const fromDate = findNearestAnteriorMonday( this.selectedDate )

        return this.updateTimeline(fromDate)
      })
      .then(() => {
        const hasMiscLeaveFlag = or(
          this.hasFeature(this.FEATURES.FLA_2_AUTRES_ABSENCES),
          this.hasACL(this.ACLS.OTHER_LEAVE_ENABLED)
        )

        const hasRemoteWorkFlag
          = this.hasFeature(this.FEATURES.FLA_4_TELETRAVAIL)

        this.options_leaveType = generateLeaveOptionsForFilters(
          this.parametersModule,
          hasMiscLeaveFlag,
          hasRemoteWorkFlag
        )
      })
      .catch(tapCatch(err => {
        console.warn('fetch', err)
        this.ToastBuilder
          .error(toString(err))
          .title(`Planning des équipes`)
          .show()
      }))
    },
    /**
     * @desc
     *    Main business calculating the displayed timeline
     *
     *    (?) KISS algorithm here, implies:
     *        - starting range date will always be a monday
     *        - ending range date will always be a sunday
     *
     *        This will allow a timeline and UI focussed on weeks (which could be
     *        less confusing that if start the timeline is changed on any weekday)
     */
    updateTimeline(
      fromDate :string, // "YYYY-MM-DD"
    ) {

      const pageWidth = window.document.body.offsetWidth

      const mSidebarWidth = querySelector<HTMLElement>('.the-menu-sidebar')
        .map(el => el.offsetWidth)

      if (Maybe.isNothing(mSidebarWidth)) {
        console.error('no sidebar width retrieved, should not happen')
        return Promise.resolve()
      }

      const sidebarWidth = Maybe.fromJust(mSidebarWidth)
      const containerWidth = subtract(pageWidth, sidebarWidth)

      /* In case of a very small container, we will display only one week, the Math.max function
      will prevent the week count to be less than 1 which previously failed the subsequent request */
      const maximumWeekCount = Math.max(calculateMaximumWeekCount(containerWidth), 1)

      const dateMax = findPeriodLastSunday(
        fromDate,
        maximumWeekCount
      )

      this.dateMin = fromDate
      this.dateMax = dateMax

      return this.updateData()
        .then(() => {
          this.dateList = getDateList(fromDate, dateMax)
          this.weekList = getWeekList(fromDate, dateMax)
        })
    },
    updateData() {
      // no filter set → select every service/matricule available
      const isFilterSet = lengthNotEq(0, this.selected_serviceSlug)

      const serviceList = (() => {
        if (not(isFilterSet))
          return pluck('uuidTeam')(this.serviceList)

        return this.selected_serviceId
      })()

      const matriculeList = (() => {
        if (not(isFilterSet))
          return []

        return this.selected_matriculeId
      })()

      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        dateMin: this.dateMin,
        dateMax: this.dateMax,
        leaveType_List: this.selected_leaveType,
        matriculeList,
        serviceList,
      }

      this.isPending = true;

      return this.store_scheduling_v2.readSchedulingList(options)
        .then(
          (schedulingList) => {
            this.displayedSchedulingDictionary = castToDictionary(schedulingList)

            this.displayedServiceCollection = (() => {
              if (not(isFilterSet))
                return this.serviceList
              /// @XXX micro service not providing an output iso with model-view
              return this.getSelectedCollaboratorGroupedByService()
            })()
          }
        )
        .catch(err => {
          console.warn('updateData', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Planning des équipes`)
            .show()
        })
        .finally(() => this.isPending = false)
    },
  }
})

const getDayCount = (
  dateMin :string,
  dateMax :string
) => abs(
    dateDifference(
      'days',
      {
        inputFormat: 'YYYY-MM-DD'
      },
      dateMin,
      {
        inputFormat: 'YYYY-MM-DD'
      },
      dateMax
    ),
  )

const calculateDateObject = (
  fromDate :string,
  additionalDayCount :number
) => pipe_(
    fromDate,
    parseDatetime(
      {
        inputFormat: 'YYYY-MM-DD',
        outputFormat: 'YYYY-MM-DD',
        additional: [ additionalDayCount, 'days' ]
      }
    ),
    datetimeToDateObject(
      {
        inputFormat: 'YYYY-MM-DD'
      }
    )
  )

function getDateList(
  dateMin :string, // "YYYY-MM-DD"
  dateMax :string, // "YYYY-MM-DD"
) :Date[] {

  const dayCount = inc( getDayCount(dateMin, dateMax) )
  const dateList = [] as Date[]

  times(
    idx => {
      const day = calculateDateObject(dateMin, idx)

      // (mutation)
      dateList.push( day )
    },
    dayCount
  )

  return dateList
}

function getWeekList(
  dateMin :string, // "YYYY-MM-DD"
  dateMax :string, // "YYYY-MM-DD"
) :Week[] {

  const dayCount = inc( getDayCount(dateMin, dateMax) )
  const weekCount = divide(dayCount, 7)
  const weekList = [] as Week[]

  times(
    idx => {
      const mondayIndex = multiply(7, idx)
      const sundayIndex = pipe_(
        idx,
        n => multiply(7, n),
        n => add(6, n)
      )

      const monday = parseDatetime(
        {
          inputFormat: 'YYYY-MM-DD',
          outputFormat: 'YYYY-MM-DD',
          additional: [ mondayIndex, 'days' ]
        },
        dateMin
      )

      const sunday = parseDatetime(
        {
          inputFormat: 'YYYY-MM-DD',
          outputFormat: 'YYYY-MM-DD',
          additional: [ sundayIndex, 'days' ]
        },
        dateMin
      )

      const number = parseDatetime(
        {
          outputFormat: 'w'
        },
        monday
      )

      weekList.push({ monday, sunday, number })
    },
    weekCount
  )

  return weekList
}

function findNearestAnteriorMonday(
  fromDate :string, // "YYYY-MM-DD"
) :string // "YYYY-MM-DD"
{
  const dayIndex = pipe_(
    fromDate,
    parseDatetime(
      {
        inputFormat: 'YYYY-MM-DD',
        outputFormat: 'd'
      },
    ),
    parseInt
  )

  const toSubstract = (() => {
    const monday  = mod(1, 7)
    const day     = mod(dayIndex, 7)
    const week    = 7

    switch (dayIndex) {

      case 0: // (sunday → previous monday of week before)
        return monday - day - week

      default:
        return monday - day
    }
  })()

  return parseDatetime(
    {
      inputFormat: 'YYYY-MM-DD',
      outputFormat: 'YYYY-MM-DD',
      additional: [ toSubstract, 'days' ]
    },
    fromDate
  )
}
/**
 * @desc Calculate the maximum number of weeks that can populate the DOM
 *    Element container (ie. without triggering horizontal scrolling)
 */
function calculateMaximumWeekCount(
  containerWidth :number, // in pixels
) {

  const leftColumnRemoved = pipe_(
    containerWidth,
    n => subtract(
      n,
      multiply(CONTAINER_PADDING, 2)
    ),
    n => subtract(n, DETAILS_COLUMN),
    n => subtract(
      n,
      divide(GUTTER, 2)
    )
  )

  const weekTemplateSize = add(
    multiply(CELL_SIZE, 7),
    multiply(GUTTER, 7)
  )

  return pipe_(
    leftColumnRemoved,
    n => divide(n, weekTemplateSize),
    n => floor(n),
    n => toInteger(n)
  )
}
/**
 * @desc Providing a number of weeks with a starting monday, will return the
 *    ending sunday
 */
function findPeriodLastSunday(
  startingMonday :string, // "YYYY-MM-DD"
  weekCount :number
) {
  const dayCount = dec( multiply(weekCount, 7) )

  return parseDatetime(
    {
      inputFormat: 'YYYY-MM-DD',
      outputFormat: 'YYYY-MM-DD',
      additional: [ dayCount, 'days' ]
    },
    startingMonday
  )
}

function previousMonday(
  monday :string // "YYYY-MM-DD"
) :string // "YYYY-MM-DD"
{
  return parseDatetime(
    {
      inputFormat: 'YYYY-MM-DD',
      outputFormat: 'YYYY-MM-DD',
      additional: [ -7, 'days' ]
    },
    monday
  )
}

function nextMonday(
  monday :string // "YYYY-MM-DD"
) :string // "YYYY-MM-DD"
{
  return parseDatetime(
    {
      inputFormat: 'YYYY-MM-DD',
      outputFormat: 'YYYY-MM-DD',
      additional: [ 7, 'days' ]
    },
    monday
  )
}

</script>
